import { Helmet } from 'react-helmet-async';
import { canonicalUrl, DEFAULT_OG_IMAGE, DEFAULT_OG_IMAGE_ALT } from '../lib/site-url';

export interface SeoMetaProps {
  title?: string;
  description?: string;
  /** Volledige canonical URL (heeft voorrang boven `path`). */
  canonical?: string;
  /** Pad zoals `/prijzen` — wordt absolute canonical + og:url. */
  path?: string;
  /** Voor account/admin: niet indexeren door zoekmachines. */
  noindex?: boolean;
  /** Open Graph / Twitter afbeelding (absolute URL aanbevolen). */
  ogImage?: string;
  /** Beschrijving van de OG-afbeelding (og:image:alt / Twitter). */
  ogImageAlt?: string;
  /** Komma-gescheiden keywords (blog / landingspagina’s). */
  keywords?: string;
  /** Open Graph type — `article` voor blogposts. */
  ogType?: 'website' | 'article';
  /** Alleen bij `ogType="article"`: structured hints voor social. */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
    author?: string;
    tags?: string[];
  };
  /** JSON-LD object(en) als `application/ld+json` (blog / FAQ). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = 'CalqTool™';
const DEFAULT_DESCRIPTION =
  'Bouwkosten berekenen voor renovatie, transformatie en splitsing. Deterministische prijsbibliotheek, begroting en export — Nederlandse bouwsector.';

export function SeoMeta({
  title,
  description,
  canonical,
  path,
  noindex,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  keywords,
  ogType = 'website',
  article,
  jsonLd,
}: SeoMetaProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} – ${DEFAULT_DESCRIPTION}`;
  const desc = description ?? DEFAULT_DESCRIPTION;
  const canonicalHref = canonical ?? (path != null ? canonicalUrl(path) : undefined);
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';
  const ldBlocks = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <html lang="nl" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords?.trim() ? <meta name="keywords" content={keywords.trim()} /> : null}
      <meta name="robots" content={robots} />
      {canonicalHref ? <link rel="canonical" href={canonicalHref} /> : null}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={ogType} />
      {canonicalHref ? <meta property="og:url" content={canonicalHref} /> : null}
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      {ogType === 'article' && article?.publishedTime ? (
        <meta property="article:published_time" content={article.publishedTime} />
      ) : null}
      {ogType === 'article' && article?.modifiedTime ? (
        <meta property="article:modified_time" content={article.modifiedTime} />
      ) : null}
      {ogType === 'article' && article?.section ? (
        <meta property="article:section" content={article.section} />
      ) : null}
      {ogType === 'article' && article?.author ? <meta property="article:author" content={article.author} /> : null}
      {ogType === 'article' && article?.tags?.length
        ? article.tags.map((t) => <meta key={t} property="article:tag" content={t} />)
        : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      {ldBlocks.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
